/**
 * ProjectIcon — DRY lookup from (icon name, color token) to a styled glyph.
 *
 * Phase 4.2 refresh (June 13, 2026 — UI review #ProjectIcon-v2):
 * pre-refresh pattern was a filled saturated tile (``bg-violet-100 +
 * text-violet-600`` ramps) that collided with the warm-cream / ink-first
 * Ando v18 palette and clashed with the orange Companion mark when both
 * appeared at the same time (commercialista-orange project next to the
 * brand-orange ``Sparkles`` blob → two orange circles same family).
 *
 * The new visual contract follows the Linear / Notion 3.0 pattern:
 *   - Tile      : ``bg-cream/60`` (semi-transparent warm surface) +
 *                 ``border border-hairline`` ring, ``rounded-md``.
 *   - Glyph     : monochrome ``text-ink-2``, ``strokeWidth=1.5`` — sits
 *                 quietly inside the chrome instead of competing with the
 *                 brand mark.
 *   - Accent dot: 6×6 absolute bottom-right, ``ring-1 ring-cream`` so it
 *                 separates cleanly on the lighter cream surfaces.
 *
 * API back-compat: every prop on the previous component is preserved
 * (``iconName``, ``colorToken``, ``size``, ``className``, ``bare``) so
 * the three consumers (ProjectPill, NewProjectDialog preview, ProjectHub
 * card) keep working without an edit. ``bare`` falls through to the
 * legacy "just the glyph" render — used by the NewProjectDialog colour
 * picker swatches where the dot would be visual noise.
 *
 * The icon allowlist is intentionally short: 8 archetypes covering common
 * workspace metaphors. Adding new ones = expand ``PROJECT_ICONS`` (the BE
 * stores the lucide name as a free string but the picker UI is gated).
 *
 * Color is a token from ``PROJECT_COLORS``. The legacy ``fg`` + ``bg``
 * fields are removed (no consumer reads them outside this file — verified
 * via grep before the refactor); ``dot`` replaces them as the single
 * accent token. Token names stay stable so the BE / DB rows continue to
 * round-trip unchanged.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  BarChart3,
  Briefcase,
  Code,
  Folder,
  type LucideIcon,
  Mail,
  PenTool,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import type * as React from "react";

import { cn } from "@ando/ui";

// ── Icons ───────────────────────────────────────────────────────────────

export interface ProjectIconDef {
  readonly name: string;
  readonly Component: LucideIcon;
  /** Localisable label for the picker — resolve via ``i18n._()``. */
  readonly label: MessageDescriptor;
}

export const PROJECT_ICONS: readonly ProjectIconDef[] = [
  { name: "Folder", Component: Folder, label: msg`Cartella` },
  { name: "Briefcase", Component: Briefcase, label: msg`Lavoro` },
  { name: "Sparkles", Component: Sparkles, label: msg`Idee` },
  { name: "BarChart3", Component: BarChart3, label: msg`Analisi` },
  { name: "Mail", Component: Mail, label: msg`Comunicazione` },
  { name: "ShoppingCart", Component: ShoppingCart, label: msg`Vendite` },
  { name: "Code", Component: Code, label: msg`Tecnico` },
  { name: "PenTool", Component: PenTool, label: msg`Creativo` },
];

/** O(1) lookup map — exported so other surfaces (Sidebar) can reuse. */
export const LUCIDE_BY_NAME: Readonly<Record<string, LucideIcon>> = Object.fromEntries(
  PROJECT_ICONS.map((i) => [i.name, i.Component]),
);

export const DEFAULT_PROJECT_ICON_NAME = "Folder" as const;

export function resolveProjectIcon(name: string | null | undefined): LucideIcon {
  if (name == null) return Folder;
  return LUCIDE_BY_NAME[name] ?? Folder;
}

// ── Colors ──────────────────────────────────────────────────────────────

export interface ProjectColorDef {
  readonly token: string;
  /** Localisable label for the picker — resolve via ``i18n._()``. */
  readonly label: MessageDescriptor;
  /**
   * Tailwind class for the 6×6 accent dot. Single source of truth for
   * the project's identity colour — appears bottom-right of the tile.
   * Token stays stable when the BE round-trips ``color`` so old rows
   * don't reshuffle visually after the refresh.
   */
  readonly dot: string;
}

/**
 * Brand-aligned identity tokens. ``orange`` is the brand default
 * (matches the legacy v1 pill); other tones use the Tailwind 500-ramp
 * which renders saturated enough to read at 6×6 without dragging the
 * Material-y 100/600 ramp that the previous filled-tile pattern leaked
 * into the warm-cream chrome.
 */
export const PROJECT_COLORS: readonly ProjectColorDef[] = [
  { token: "orange", label: msg`Arancione`, dot: "bg-ando-orange" },
  { token: "violet", label: msg`Viola`, dot: "bg-violet-500" },
  { token: "blue", label: msg`Blu`, dot: "bg-sky-500" },
  { token: "turquoise", label: msg`Turchese`, dot: "bg-teal-500" },
  { token: "pink", label: msg`Rosa`, dot: "bg-pink-500" },
  { token: "green", label: msg`Verde`, dot: "bg-emerald-500" },
  { token: "red", label: msg`Rosso`, dot: "bg-rose-500" },
  { token: "yellow", label: msg`Giallo`, dot: "bg-amber-500" },
];

export const DEFAULT_PROJECT_COLOR_TOKEN = "orange" as const;

const COLOR_BY_TOKEN: Readonly<Record<string, ProjectColorDef>> = Object.fromEntries(
  PROJECT_COLORS.map((c) => [c.token, c]),
);

export function resolveProjectColor(token: string | null | undefined): ProjectColorDef {
  if (token == null) return COLOR_BY_TOKEN.orange!;
  return COLOR_BY_TOKEN[token] ?? COLOR_BY_TOKEN.orange!;
}

// ── Component ───────────────────────────────────────────────────────────

export interface ProjectIconProps {
  readonly iconName: string | null;
  readonly colorToken: string | null;
  /** Size of the inner SVG in px. Default 11 (matches the v1 ProjectPill). */
  readonly size?: number;
  /** Extra classes on the outer tile (e.g. ``h-6 w-6`` for a larger chip). */
  readonly className?: string;
  /**
   * When true, render the glyph alone with no tile, no border, no dot —
   * useful for picker swatches and tight inline contexts where the chrome
   * would be noise. Default ``false``.
   */
  readonly bare?: boolean;
  /**
   * When true, suppress the accent dot but keep the tile + glyph. Used
   * by the compact ProjectPill rail (36×36 trigger), where a 6px dot
   * sitting inside a 20px tile reads as decoration rather than identity.
   * Default ``false``.
   */
  readonly hideDot?: boolean;
}

export const ProjectIcon: React.FC<ProjectIconProps> = ({
  iconName,
  size = 11,
  className,
}) => {
  const Icon = resolveProjectIcon(iconName);
  // Pencil-line identity (June 2026): a bare, monochrome thin-line glyph —
  // no box, no colour, no accent dot. Matches the brand's outline aesthetic
  // and keeps the UI calm (no per-project colour noise). ``colorToken`` is
  // retained on the props for API back-compat but no longer rendered.
  return (
    <span
      aria-hidden
      className={cn("inline-flex items-center justify-center text-ink-2", className)}
    >
      <Icon size={size} strokeWidth={1.5} />
    </span>
  );
};

ProjectIcon.displayName = "ProjectIcon";
